/**
 * TimezoneContext - Contexto global para configuração de fuso horário
 *
 * Permite ao usuário selecionar em qual timezone deseja visualizar as métricas.
 * Por padrão: America/Sao_Paulo (UTC-3)
 *
 * O timezone afeta:
 * - Queries de vendas (paid_at)
 * - Exibição de datas/horários
 * - Dados de Meta Ads
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBroadcastChannel, type BroadcastMessage } from '@/hooks/useBroadcastChannel';

/**
 * Lista de timezones disponíveis
 * Cada entrada contém: id (IANA), label (nome amigável), offset (para cálculos)
 */
export interface TimezoneOption {
  id: string;
  label: string;
  offset: number; // Offset em horas (negativo para oeste de UTC)
  region: string;
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  // Américas
  { id: 'America/Sao_Paulo', label: 'São Paulo (BRT)', offset: -3, region: 'Américas' },
  { id: 'America/New_York', label: 'Nova York (EST/EDT)', offset: -5, region: 'Américas' },
  { id: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)', offset: -8, region: 'Américas' },
  { id: 'America/Chicago', label: 'Chicago (CST/CDT)', offset: -6, region: 'Américas' },
  { id: 'America/Denver', label: 'Denver (MST/MDT)', offset: -7, region: 'Américas' },
  { id: 'America/Mexico_City', label: 'Cidade do México (CST)', offset: -6, region: 'Américas' },
  { id: 'America/Buenos_Aires', label: 'Buenos Aires (ART)', offset: -3, region: 'Américas' },
  { id: 'America/Lima', label: 'Lima (PET)', offset: -5, region: 'Américas' },
  { id: 'America/Bogota', label: 'Bogotá (COT)', offset: -5, region: 'Américas' },

  // Europa
  { id: 'Europe/London', label: 'Londres (GMT/BST)', offset: 0, region: 'Europa' },
  { id: 'Europe/Paris', label: 'Paris (CET/CEST)', offset: 1, region: 'Europa' },
  { id: 'Europe/Berlin', label: 'Berlim (CET/CEST)', offset: 1, region: 'Europa' },
  { id: 'Europe/Madrid', label: 'Madri (CET/CEST)', offset: 1, region: 'Europa' },
  { id: 'Europe/Rome', label: 'Roma (CET/CEST)', offset: 1, region: 'Europa' },
  { id: 'Europe/Amsterdam', label: 'Amsterdã (CET/CEST)', offset: 1, region: 'Europa' },
  { id: 'Europe/Lisbon', label: 'Lisboa (WET/WEST)', offset: 0, region: 'Europa' },
  { id: 'Europe/Moscow', label: 'Moscou (MSK)', offset: 3, region: 'Europa' },

  // Ásia/Pacífico
  { id: 'Asia/Tokyo', label: 'Tóquio (JST)', offset: 9, region: 'Ásia/Pacífico' },
  { id: 'Asia/Shanghai', label: 'Xangai (CST)', offset: 8, region: 'Ásia/Pacífico' },
  { id: 'Asia/Singapore', label: 'Singapura (SGT)', offset: 8, region: 'Ásia/Pacífico' },
  { id: 'Asia/Dubai', label: 'Dubai (GST)', offset: 4, region: 'Ásia/Pacífico' },
  { id: 'Asia/Kolkata', label: 'Mumbai (IST)', offset: 5.5, region: 'Ásia/Pacífico' },
  { id: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)', offset: 8, region: 'Ásia/Pacífico' },
  { id: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)', offset: 10, region: 'Ásia/Pacífico' },

  // UTC
  { id: 'UTC', label: 'UTC (Coordenado)', offset: 0, region: 'Universal' },
];

// Timezone padrão
export const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

interface TimezoneContextType {
  timezone: string;
  timezoneInfo: TimezoneOption;
  setTimezone: (tz: string) => void;
  // Utilitários de conversão
  getUtcOffset: () => number;
  formatInTimezone: (date: Date | string) => string;
  formatDateTimeInTimezone: (date: Date | string) => string;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

// Chave do localStorage
const TIMEZONE_STORAGE_KEY = 'd12-timezone';

/**
 * Carrega o timezone do localStorage
 */
function loadTimezoneFromStorage(): string {
  try {
    const stored = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (stored && TIMEZONE_OPTIONS.some(tz => tz.id === stored)) {
      return stored;
    }
  } catch {
    // Ignorar erros de localStorage
  }
  return DEFAULT_TIMEZONE;
}

/**
 * Salva o timezone no localStorage
 */
function saveTimezoneToStorage(timezone: string) {
  try {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone);
  } catch {
    // Ignorar erros de localStorage
  }
}

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Handler para mensagens de outras abas
  const handleCrossTabMessage = useCallback((message: BroadcastMessage) => {
    if (message.type !== 'TIMEZONE_CHANGED') return;

    const payload = message.payload as { timezone: string } | undefined;
    if (payload?.timezone && TIMEZONE_OPTIONS.some(tz => tz.id === payload.timezone)) {
      setTimezoneState(payload.timezone);

      // Invalidar todas as queries que dependem de timezone
      invalidateTimezoneQueries(queryClient);
    }
  }, [queryClient]);

  // Inicializar BroadcastChannel
  const { postMessage } = useBroadcastChannel(handleCrossTabMessage);

  // Estado do timezone
  const [timezone, setTimezoneState] = useState<string>(loadTimezoneFromStorage);

  // Função para atualizar o timezone
  const setTimezone = useCallback((tz: string) => {
    if (!TIMEZONE_OPTIONS.some(opt => opt.id === tz)) {
      console.warn(`[Timezone] Timezone inválido: ${tz}`);
      return;
    }

    setTimezoneState(tz);
    saveTimezoneToStorage(tz);

    // Notificar outras abas
    postMessage('TIMEZONE_CHANGED', { timezone: tz });

    // Invalidar queries que dependem de timezone
    invalidateTimezoneQueries(queryClient);
  }, [queryClient, postMessage]);

  // Escutar mudanças no localStorage (fallback)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== TIMEZONE_STORAGE_KEY || !event.newValue) return;

      if (TIMEZONE_OPTIONS.some(tz => tz.id === event.newValue)) {
        setTimezoneState(event.newValue);
        invalidateTimezoneQueries(queryClient);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [queryClient]);

  // Obter informações do timezone atual
  const timezoneInfo = TIMEZONE_OPTIONS.find(tz => tz.id === timezone) || TIMEZONE_OPTIONS[0];

  // Obter offset UTC
  const getUtcOffset = useCallback(() => {
    return timezoneInfo.offset;
  }, [timezoneInfo]);

  // Formatar data no timezone selecionado
  const formatInTimezone = useCallback((date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR', { timeZone: timezone });
  }, [timezone]);

  // Formatar data e hora no timezone selecionado
  const formatDateTimeInTimezone = useCallback((date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('pt-BR', {
      timeZone: timezone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [timezone]);

  return (
    <TimezoneContext.Provider
      value={{
        timezone,
        timezoneInfo,
        setTimezone,
        getUtcOffset,
        formatInTimezone,
        formatDateTimeInTimezone,
      }}
    >
      {children}
    </TimezoneContext.Provider>
  );
}

/**
 * Hook para acessar o contexto de timezone
 */
export function useTimezone() {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
}

/**
 * Invalida queries que dependem de timezone
 */
function invalidateTimezoneQueries(queryClient: ReturnType<typeof useQueryClient>) {
  // Vendas e comissões
  queryClient.invalidateQueries({ queryKey: ['financial-data'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard-vendas'] });
  queryClient.invalidateQueries({ queryKey: ['platform-commissions'] });
  queryClient.invalidateQueries({ queryKey: ['platform-refunds'] });
  queryClient.invalidateQueries({ queryKey: ['platform-chargebacks'] });
  queryClient.invalidateQueries({ queryKey: ['daily-breakdown'] });
  queryClient.invalidateQueries({ queryKey: ['sales'] });

  // Meta Ads
  queryClient.invalidateQueries({ queryKey: ['dashboard-metaads-daily'] });
  queryClient.invalidateQueries({ queryKey: ['metaads-dashboard'] });
  queryClient.invalidateQueries({ queryKey: ['metaads-cashflow'] });

  // Outros
  queryClient.invalidateQueries({ queryKey: ['dashboard-fulfillment'] });
}
